// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
import PermissionConstants from "../../../configs/PermissionConstants";
// Service
import { get, post, put } from "../../../services/FetchHelper";
import { createFolder } from "../../../services/AwsService";
import SpinService from "../../../services/SpinService";
// Utils
import {
    InitialSchoolState,
    SchoolSchema,
    FormFields,
} from "./School.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
// Hooks
import usePermissions from "../../../hooks/usePermissions";

const School = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [school, setSchool] = useState(InitialSchoolState);
    const [formFields, setFormFields] = useState(FormFields);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "autocomplete")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const getAccounts = async () => {
        const response = await get(EntityConstants.ACCOUNTS, "max=all");
        const data = await response.json();
        attachOptions("debit_account_id", data);
        attachOptions("credit_account_id", data);
        spinService.off();
    };

    const getAcademicYears = async () => {
        const response = await get(EntityConstants.ACADEMIC_YEARS, "max=all");
        const data = await response.json();
        spinService.off();
        attachOptions("academic_year_id", data);
    };

    const getSchool = async (id) => {
        const response = await get(EntityConstants.SCHOOLS + `/${id}`);
        const data = await response.json();
        spinService.off();
        setSchool(data);
    };

    const create_aws_folders = (school_id) => {
        createFolder(`${school_id}/student_profiles`);
        createFolder(`${school_id}/student_documents`)
    };

    const handleToggleStatus = async (school_id, current_status) => {
        await post(`${EntityConstants.TOGGLE_SCHOOL_STATUS}/${school_id}`, {});
        toast.success(current_status ? "School deactivated." : "School activated.");
        onClose();
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const isNewSchool = values.id === undefined;
        const url = isNewSchool ? EntityConstants.SCHOOLS : EntityConstants.SCHOOLS + `/${values.id}`;
        const method = isNewSchool ? post : put;
        const response = await method(url, { school: values });
        spinService.off();
        if ([200, 201].indexOf(response.status) > -1) {
            const data = await response.json();
            isNewSchool && create_aws_folders(data.id)
            toast.success(isNewSchool ? MessageContants.SCHOOL_CREATED : MessageContants.SCHOOL_UPDATED);
            onClose();
        }
    };

    useEffect(() => {
        spinService.on();
        getAccounts();
        getAcademicYears();
        if (id !== undefined) {
            getSchool(Number(id));
        }
    }, [id]);

    return (
        <Formik
            enableReinitialize
            initialValues={school}
            validationSchema={SchoolSchema}
            onSubmit={(values) => handleSubmit(values)}
        >
            {({
                values,
                touched,
                errors,
                dirty,
                isSubmitting,
                handleChange,
                handleBlur,
                handleSubmit,
            }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StyledDrawerForm>
                        <Grid
                            container
                            maxWidth="sm"
                            rowSpacing={1}
                            columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                        >
                            {formFields.map((formField, index) => {
                                return (
                                    <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                        <GoForm
                                            config={formField}
                                            values={values}
                                            touched={touched}
                                            errors={errors}
                                            handleChange={handleChange}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                        <Box sx={{ p: 2, textAlign: "right" }}>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<Iconify icon="eva:close-circle-outline" />}
                                onClick={() => onClose()}
                            >
                                Close
                            </Button>
                            &nbsp;&nbsp;
                            {
                                ((values.id === undefined && permissions.has(PermissionConstants.SCHOOL_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.SCHOOL_UPDATE))) &&
                                <Button
                                    variant="contained"
                                    startIcon={<Iconify icon="eva:save-fill" />}
                                    type="submit"
                                >
                                    {values.id === undefined ? 'Save' : 'Update'}
                                </Button>}
                            &nbsp;&nbsp;
                            {values.id !== undefined && permissions.has(PermissionConstants.SCHOOL_UPDATE) && (
                                <Button
                                    variant="outlined"
                                    color={values.status === false ? "success" : "error"}
                                    startIcon={<Iconify icon={values.status === false ? "mdi:eye-outline" : "mdi:eye-off-outline"} />}
                                    onClick={() => handleToggleStatus(values.id, values.status)}
                                >
                                    {values.status === false ? "Activate" : "Deactivate"}
                                </Button>
                            )}
                        </Box>
                    </StyledDrawerForm>
                </Form>
            )}
        </Formik>
    );
};

School.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default School;
